/**
 * Übersetzungs-Tab Komponente mit OCR-Funktionalität
 */

import React from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '../hooks/useTranslation';
import { useOCR } from '../hooks/useOCR';
import { useI18n } from '../hooks/useI18n';
import { useAppAppearance } from '../theme/AppAppearanceContext';
import {
  honeycombClearIconButtonSx,
  isHoneycombAppearance,
  isHoneycombLightAppearance,
} from '../theme/appAppearance';
import {
  hologramContainedButtonSx,
  hologramFieldSx,
  hologramIconButtonSx,
} from '../theme/hologramChrome';
import { LANGUAGES } from '../utils/constants';

export function TranslationTab() {
  const {
    inputText,
    setInputText,
    translatedText,
    targetLang,
    setTargetLang,
    isTranslating,
    anonymized,
    error,
    translate,
    reset,
  } = useTranslation();

  const { t } = useI18n();
  const theme = useTheme();
  const { appearance, hologram } = useAppAppearance();
  const accent = theme.palette.primary.main;
  const fieldSx = hologram ? hologramFieldSx(accent) : {};
  const containedSx = hologram ? hologramContainedButtonSx(accent) : {};
  const iconChrome = hologram ? hologramIconButtonSx(accent) : null;
  const clearIconSx = hologram
    ? hologramIconButtonSx(accent)
    : isHoneycombAppearance(appearance) && !isHoneycombLightAppearance(appearance)
      ? honeycombClearIconButtonSx()
      : {
          bgcolor: 'error.main',
          color: 'white',
          '&:hover': { bgcolor: 'error.dark' },
          '&:disabled': { bgcolor: 'action.disabledBackground' },
        };

  const {
    isProcessing: isOCRProcessing,
    progress: ocrProgress,
    error: ocrError,
    fileInputRef,
    openCamera,
    handleFileSelect,
  } = useOCR();

  const handleOCRSuccess = (text) => {
    // Füge Text zur bestehenden Eingabe hinzu (oder ersetze, wenn leer)
    if (inputText) {
      setInputText(inputText + '\n\n' + text);
    } else {
      setInputText(text);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
      {/* Versteckter File Input für Kamera/Galerie */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e, handleOCRSuccess)}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            label={t.translation.inputLabel}
            variant="outlined"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.875rem', md: '1rem' }
              },
              ...fieldSx,
            }}
            multiline
            minRows={8}
            maxRows={24}
            fullWidth
          />
          <Box sx={{ 
            position: 'absolute',
            bottom: { xs: 8, md: 8 },
            right: { xs: 8, md: 8 },
            display: 'flex',
            gap: 1,
            zIndex: 1,
          }}>
            <Tooltip title={t.translation.cameraTooltip}>
              <IconButton
                onClick={() => {
                  reset();
                  openCamera();
                }}
                disabled={isOCRProcessing || isTranslating}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  width: 44,
                  height: 44,
                  p: 0,
                  ...(iconChrome || {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'action.disabledBackground' },
                  }),
                }}
              >
                <CameraAltIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.common.clearAll}>
              <IconButton
                onClick={reset}
                disabled={isOCRProcessing || isTranslating || (!inputText && !translatedText)}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  width: 44,
                  height: 44,
                  p: 0,
                  ...(iconChrome || {
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                    '&:disabled': { bgcolor: 'action.disabledBackground' },
                  }),
                }}
              >
                <ClearIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {isOCRProcessing && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={ocrProgress} />
            <Alert severity="info" sx={{ mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              {t.translation.ocrProgress.replace('{progress}', ocrProgress)}
            </Alert>
          </Box>
        )}
        
        {ocrError && (
          <Alert severity="error" sx={{ mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            {t.translation.ocrError.replace('{error}', ocrError)}
          </Alert>
        )}
        
        {anonymized && (
          <Alert severity="info" sx={{ mt: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            {t.common.anonymizedNotice.replace('{service}', t.common.translationService)}
          </Alert>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: { xs: 2, sm: 0 }
        }}>
          <Button
            variant="contained"
            color="primary"
            onClick={translate}
            disabled={isTranslating || !inputText || isOCRProcessing}
            sx={{
              minWidth: { xs: '100%', sm: 200 },
              minHeight: 44,
              fontWeight: 600,
              letterSpacing: 0.5,
              mr: { sm: 4, xs: 0 },
              mb: { xs: 1, sm: 0 },
              fontSize: { xs: '0.9375rem', md: '1rem' },
              ...containedSx,
            }}
          >
            {isTranslating ? <CircularProgress size={24} /> : t.translation.button}
          </Button>
          
          <FormControl size="small" sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}>
            <InputLabel id="target-lang-label">{t.common.targetLang}</InputLabel>
            <Select
              labelId="target-lang-label"
              value={targetLang}
              label={t.common.targetLang}
              onChange={e => setTargetLang(e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  minHeight: { xs: 48, md: 40 }
                }
              }}
            >
              {LANGUAGES.map(lang => (
                <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  {t.languages[lang.code]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label={t.translation.outputLabel}
          variant="outlined"
          value={translatedText}
          sx={{
            width: '100%',
            mt: 2,
            '& .MuiInputBase-root': {
              fontSize: { xs: '0.875rem', md: '1rem' }
            },
            '& .MuiInputBase-input': {
              overflow: 'auto !important',
              maxHeight: '70vh',
            },
            ...fieldSx,
          }}
          multiline
          minRows={8}
          InputProps={{ 
            readOnly: true,
            sx: {
              maxHeight: '70vh',
              overflow: 'auto',
            }
          }}
          fullWidth
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
